import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { StoreProduct } from '@/pages/StoreProduct';
import { ProductType } from '@/entities/product';
import {
  fetchShopifyProducts,
  fetchShopifyProduct,
  transformShopifyProducts,
  transformShopifyProduct,
} from '@/entities/shopify';
import { PRODUCT_RENDERING_CONFIG } from '@/shared/config';
import { deviceType } from '@/shared/constants';
import { getPaths, parseSSGParams } from '@/shared/helpers';
import { i18n } from 'next-i18next.config';

interface ProductPageProps {
  product: ProductType;
  products: ProductType[];
}

export const getStaticProps: GetStaticProps<ProductPageProps> = async (context) => {
  const { device } = parseSSGParams(context.params);
  const { slug } = context.params as { slug: string };

  const isValidPath = Object.values(deviceType).includes(device);

  if (!isValidPath) {
    return {
      notFound: true,
    };
  }

  try {
    const shopifyProduct = await fetchShopifyProduct(slug);
    if (!shopifyProduct) {
      return {
        notFound: true,
      };
    }

    const product = transformShopifyProduct(shopifyProduct);

    const allShopifyProducts = await fetchShopifyProducts();
    const products =
      allShopifyProducts && allShopifyProducts.length > 0
        ? transformShopifyProducts(allShopifyProducts).filter((p: any) => p.slug !== slug)
        : [];

    return {
      props: {
        ...(await serverSideTranslations(context.locale || i18n.defaultLocale, ['common'])),
        product,
        products,
      },
      revalidate: 60,
    };
  } catch (error) {
    console.error(`[StoreProduct] Error fetching from Shopify for slug "${slug}":`, error);
    return {
      notFound: true,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  let productParams: Array<{ slug: string }> = [];

  try {
    const shopifyProducts = await fetchShopifyProducts();
    if (shopifyProducts && shopifyProducts.length > 0) {
      productParams = shopifyProducts.map((product) => ({ slug: product.handle }));
    } else {
    }
  } catch (error) {
    console.error('[StoreProduct Paths] Error fetching from Shopify:', error);
  }

  const paths = getPaths(productParams);

  return {
    paths,
    fallback: PRODUCT_RENDERING_CONFIG.fallback,
  };
};

export default StoreProduct;
